import React, { useState, useEffect } from 'react';
import { FiFileText, FiEdit, FiCheck, FiClipboard } from 'react-icons/fi';

// Define component as a named function expression before exporting
const LoadingSpinner = ({ size = 'medium', message = 'Polishing your resume' }) => {
  const [dotCount, setDotCount] = useState(1);
  
  // Size classes for the container
  const containerSizes = {
    small: 'w-24 h-24',
    medium: 'w-32 h-32',
    large: 'w-48 h-48'
  };
  
  // Size classes for the icons
  const iconSizes = {
    small: '16px',
    medium: '24px',
    large: '36px'
  };
  
  // Random fun loading messages
  const loadingMessages = [
    "Polishing your resume",
    "Making your skills shine",
    "Formatting your achievements",
    "Preparing to impress employers",
    "Adding that professional touch"
  ];
  
  // Randomly select a message if using the default
  const defaultMessage = message === 'Polishing your resume' ? 
    loadingMessages[Math.floor(Math.random() * loadingMessages.length)] : 
    message;
    
  // Modern dots animation
  useEffect(() => {
    const interval = setInterval(() => {
      setDotCount((prev) => (prev % 3) + 1);
    }, 500);
    
    return () => clearInterval(interval);
  }, []);
  
  // Generate dots based on current state
  const renderDots = () => {
    return '.'.repeat(dotCount);
  };
  
  return (
    <div className="flex flex-col items-center justify-center p-8">
      {/* Main animation container */}
      <div className={`relative ${containerSizes[size]} bg-white rounded-lg shadow-lg mb-16`}>
        {/* Paper background with decorative corner fold */}
        <div className="absolute inset-0 bg-blue-50 rounded-lg border border-blue-200">
          {/* Corner fold */}
          <div className="absolute top-0 right-0 w-6 h-6 bg-blue-100 rounded-bl-lg shadow-sm"></div>
          <div className="absolute top-0 right-0 w-5 h-5 border-b border-l border-blue-200 rounded-bl-lg"></div>
        </div>
        
        {/* Resume lines animation */}
        <div className="absolute left-4 top-4 right-4 bottom-4 flex flex-col justify-center space-y-3">
          <div className="h-2 bg-blue-200 rounded-full w-3/4 animate-pulse"></div>
          <div className="h-2 bg-blue-300 rounded-full w-full animate-pulse" style={{ animationDelay: '0.2s' }}></div>
          <div className="h-2 bg-blue-200 rounded-full w-5/6 animate-pulse" style={{ animationDelay: '0.4s' }}></div>
          <div className="h-2 bg-blue-300 rounded-full w-2/3 animate-pulse" style={{ animationDelay: '0.6s' }}></div>
        </div>
        
        {/* Spinning pencil animation */}
        <div className="absolute -right-4 -bottom-4 flex items-center justify-center">
          <div className="relative animate-spin" style={{ animationDuration: '3s' }}>
            <div className="absolute p-2 bg-yellow-400 rounded-full shadow-lg">
              <FiEdit size={iconSizes[size]} color="#1d4ed8" strokeWidth={2} />
            </div>
          </div>
        </div>
        
        {/* Document icons orbiting */}
        <div className="absolute inset-0">
          <div className="absolute left-0 -top-6 animate-bounce" style={{ animationDuration: '2s', animationDelay: '0.3s' }}>
            <FiFileText size={iconSizes[size]} color="#3b82f6" />
          </div>
          <div className="absolute -left-6 bottom-0 animate-bounce" style={{ animationDuration: '2.3s', animationDelay: '0.7s' }}>
            <FiClipboard size={iconSizes[size]} color="#3b82f6" />
          </div>
          <div className="absolute left-1/2 -top-6 transform -translate-x-1/2 animate-bounce" style={{ animationDuration: '1.7s' }}>
            <FiCheck size={iconSizes[size]} color="#3b82f6" />
          </div>
        </div>
      </div>
      
      {/* Loading message with modern dots animation - positioned below with proper spacing */}
      {defaultMessage && (
        <div className="text-center mt-2">
          <p className="text-blue-600 font-medium text-lg">
            {defaultMessage}
            <span className="inline-block w-8 text-left">{renderDots()}</span>
          </p>
        </div>
      )}
    </div>
  );
};

// Export the component after its complete definition
export default LoadingSpinner;